import type { TovSourceStatus } from "contracts/enums/tov";
import { TOV_SOURCE_STATUS } from "contracts/enums/tov";

// Human-readable labels for the TOV source cascade (upload → parse →
// vectorize → map). Surface copy for this screen: no backend-rendered artifact
// names these states, so they stay here rather than in contracts, which owns
// only the canonical labels two processes must agree on.
const SOURCE_STATUS_LABELS: Record<TovSourceStatus, string> = {
  created: "Created",
  uploaded: "Uploaded",
  parse_requested: "Parse queued",
  parsing: "Parsing document",
  parsed: "Parsed",
  chunked: "Building embeddings",
  embedded: "Mapping evidence",
  mapped: "Mapped",
  failed: "Failed",
  superseded: "Superseded",
};

export function sourceStatusLabel(status: TovSourceStatus): string {
  return SOURCE_STATUS_LABELS[status];
}

/** Statuses still moving through the cascade — poll while any source has one. */
const NON_TERMINAL_SOURCE_STATUSES: ReadonlySet<TovSourceStatus> = new Set([
  TOV_SOURCE_STATUS.CREATED,
  TOV_SOURCE_STATUS.UPLOADED,
  TOV_SOURCE_STATUS.PARSE_REQUESTED,
  TOV_SOURCE_STATUS.PARSING,
  TOV_SOURCE_STATUS.PARSED,
  TOV_SOURCE_STATUS.CHUNKED,
  TOV_SOURCE_STATUS.EMBEDDED,
]);

/**
 * Whether any source is still moving. The one predicate every TOV view polls
 * on — it was copy-pasted into three query definitions, one of which then
 * drifted to a different interval.
 */
export function isCascadeActive(
  sources: ReadonlyArray<{ status: TovSourceStatus }>,
): boolean {
  return sources.some((source) =>
    NON_TERMINAL_SOURCE_STATUSES.has(source.status),
  );
}

/** `embedded` = a mapping run was queued and has not completed or failed. */
export function mapInFlight(
  sources: ReadonlyArray<{ status: TovSourceStatus }>,
): boolean {
  return sources.some((source) => source.status === TOV_SOURCE_STATUS.EMBEDDED);
}
